/**
 * PDF 下载工具（基于 html2pdf.js）
 * 将任意 DOM 元素渲染为 PDF 并触发下载。
 *
 * 用法：
 *   import { downloadPdf } from '@/utils/pdfDownload'
 *   downloadPdf(document.getElementById('content'), 'report.pdf')
 */

/**
 * 将指定 DOM 元素下载为 PDF。
 *
 * @param {HTMLElement} element - 需要导出为 PDF 的 DOM 元素
 * @param {string}       filename - 下载文件名（不含路径），默认 'report.pdf'
 * @param {object}       [options] - html2pdf 可选配置覆盖
 * @returns {Promise<void>}
 */
export async function downloadPdf(element, filename = 'report.pdf', options = {}) {
  if (!element) {
    console.warn('[pdfDownload] element is null, abort')
    return
  }

  // 动态加载 html2pdf，避免进入首屏 chunk
  const { default: html2pdf } = await import('html2pdf.js')

  const defaultOpts = {
    margin: [10, 10, 10, 10],
    filename,
    image: { type: 'jpeg', quality: 0.95 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      logging: false
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait'
    }
  }

  // 浅合并配置
  const combinedOpts = { ...defaultOpts, ...options }
  if (options.margin !== undefined) combinedOpts.margin = options.margin
  if (options.image !== undefined) combinedOpts.image = { ...defaultOpts.image, ...options.image }
  if (options.html2canvas !== undefined) combinedOpts.html2canvas = { ...defaultOpts.html2canvas, ...options.html2canvas }
  if (options.jsPDF !== undefined) combinedOpts.jsPDF = { ...defaultOpts.jsPDF, ...options.jsPDF }

  try {
    await html2pdf().set(combinedOpts).from(element).save()
  } catch (err) {
    console.error('[pdfDownload] Failed:', err)
    throw err
  }
}

/**
 * 等待容器内所有图片加载完成（含加载失败），避免 html2canvas 截图时图片未就绪。
 * @param {ParentNode} container
 * @returns {Promise<void>}
 */
function waitForImages(container) {
  if (!container) return Promise.resolve()
  const imgs = Array.from(container.querySelectorAll('img'))
  if (!imgs.length) return Promise.resolve()
  return Promise.all(
    imgs.map((img) => {
      if (img.complete) return Promise.resolve()
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true })
        img.addEventListener('error', resolve, { once: true })
      })
    })
  )
}

/**
 * 将一段 HTML 字符串下载为 PDF。
 *
 * 适用于「内容渲染在 Shadow DOM 内、html2canvas 无法穿透」的场景（如大模型生成 HTML 经
 * DOMPurify 净化后挂入 Shadow DOM 显示）。做法：把 HTML 写进离屏 iframe 的独立文档，
 * iframe 内坐标从 0 开始、尺寸自洽，再对其 body 截图导出，规避离屏普通 DOM 截图空白问题。
 *
 * @param {string} html - 已净化的 HTML 字符串
 * @param {string} filename - 下载文件名，默认 'report.pdf'
 * @param {object} [options] - 透传给 downloadPdf 的 html2pdf 配置覆盖
 * @returns {Promise<void>}
 */
export async function downloadHtmlAsPdf(html, filename = 'report.pdf', options = {}) {
  if (!html) {
    console.warn('[pdfDownload] html is empty, abort')
    return
  }

  const iframe = document.createElement('iframe')
  // 保留可测量尺寸，仅移出可视区（display:none 会让内容零尺寸导致截图空白）
  iframe.style.cssText = 'position:absolute;left:-10000px;top:0;width:800px;height:100px;border:0;background:#fff;'
  document.body.appendChild(iframe)
  try {
    const doc = iframe.contentDocument || iframe.contentWindow.document
    doc.open()
    doc.write(
      '<!DOCTYPE html><html><head><meta charset="utf-8">' +
      '<style>html,body{margin:0;padding:24px;background:#fff;color:#333;' +
      'font-family:PingFangSC-Regular,-apple-system,"Microsoft YaHei",sans-serif;' +
      'font-size:14px;line-height:1.8;box-sizing:border-box;}' +
      'table{width:100%;border-collapse:collapse;}' +
      'th,td{border:1px solid #e0e0e0;padding:8px 12px;}' +
      'img{max-width:100%;}</style></head><body>' + html + '</body></html>'
    )
    doc.close()

    // 等待 iframe 文档就绪 + 图片加载，并按内容撑高 iframe 以便完整截取
    await new Promise((resolve) => {
      if (doc.readyState === 'complete') resolve()
      else iframe.addEventListener('load', resolve, { once: true })
    })
    await waitForImages(doc.body)
    iframe.style.height = doc.body.scrollHeight + 'px'

    await downloadPdf(doc.body, filename, options)
  } finally {
    document.body.removeChild(iframe)
  }
}

/**
 * 将一段 HTML 字符串下载为 Word 文档（.doc）。
 *
 * 原理：Word 可识别带 Office XML 命名空间的 HTML，将其包装后以
 * application/msword 类型输出 .doc 文件，保留表格、列表、加粗等格式。
 * 与 html-docx-js 实现一致，但零依赖。
 *
 * @param {string} html - 已渲染的 HTML 字串
 * @param {string} filename - 下载文件名，默认 'report.doc'
 * @returns {void}
 */
export function downloadWord(html, filename = 'report.doc') {
  if (!html) {
    console.warn('[pdfDownload] html is empty, abort')
    return
  }

  const header =
    '<html xmlns:o="urn:schemas-microsoft-com:office:office" ' +
    'xmlns:w="urn:schemas-microsoft-com:office:word" ' +
    'xmlns="http://www.w3.org/TR/REC-html40">' +
    '<head><meta charset="utf-8"><title>报告</title>' +
    '<style>' +
    'body{margin:0;padding:24px;background:#fff;color:#333;' +
    'font-family:PingFangSC-Regular,-apple-system,"Microsoft YaHei",sans-serif;' +
    'font-size:14px;line-height:1.8;}' +
    'h1,h2,h3,h4,h5,h6{margin:16px 0 8px;}' +
    'table{width:100%;border-collapse:collapse;}' +
    'th,td{border:1px solid #e0e0e0;padding:8px 12px;}' +
    'img{max-width:100%;}' +
    '</style></head><body>'
  const footer = '</body></html>'

  const content = header + html + footer
  const blob = new Blob(['\ufeff' + content], { type: 'application/msword' })
  triggerBlobDownload(blob, filename)
}

/**
 * 将 Markdown 源文本下载为 .md 文件。
 *
 * @param {string} markdown - Markdown 源文本
 * @param {string} filename - 下载文件名，默认 'report.md'
 * @returns {void}
 */
export function downloadMarkdown(markdown, filename = 'report.md') {
  if (!markdown) {
    console.warn('[pdfDownload] markdown is empty, abort')
    return
  }

  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' })
  triggerBlobDownload(blob, filename)
}

/**
 * 触发 Blob 文件下载（内部工具）。
 * @param {Blob} blob
 * @param {string} filename
 */
function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default downloadPdf
